import React, { Suspense } from "react";
import { t } from "@client/lib/dictionary";
import { useUserStore } from "@client/store/user";
import FoscamTabPanel from "./SettingsTabPanels/FoscamTabPanel";
import LayoutTabPanel from "./SettingsTabPanels/LayoutTabPanel";
import PushTabPanel from "./SettingsTabPanels/PushTabPanel";
import RulesTabPanel from "./SettingsTabPanels/RulesTabPanel";
import UserTabPanel from "./SettingsTabPanels/UserTabPanel";
import { Spinner } from "./ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

// Líně: panel táhne Monaco (~3,7 MB chunk). Staticky importovaný by se
// stahoval při každém načtení aplikace, i když do nastavení nikdo nejde.
const ConfigTabPanel = React.lazy(() => import("./SettingsTabPanels/ConfigTabPanel"));
// Líně ze stejného důvodu: panel táhne xterm.js (~400 kB), a konzoli k AP otevře
// jeden člověk za měsíc.
const RouterOsTabPanel = React.lazy(() => import("./SettingsTabPanels/RouterOsTabPanel"));

interface SettingsProps {
  closeMethod: () => void;
}

const Settings: React.FC<SettingsProps> = ({ closeMethod }) => {
  const user = useUserStore((s) => s.user);
  const isAdmin = typeof user === "object" && user !== null && (user as { role: string }).role === "ADMIN";
  return (
    <Tabs defaultValue="user" className="w-full">
      <TabsList className="w-full justify-start">
        <TabsTrigger value="user">{t.settings.tabs.preferences}</TabsTrigger>
        <TabsTrigger value="push">{t.settings.tabs.push}</TabsTrigger>
        <TabsTrigger value="rules">{t.settings.tabs.rules}</TabsTrigger>
        {/* Konfigurace PicoW: admin-only. Nejde jen o zápis — v configu jsou hesla
            k WiFi i MQTT, takže ani čtení nepatří běžnému uživateli. Server to
            vynucuje přes requireAdmin na všech endpointech. */}
        {isAdmin && <TabsTrigger value="picow">{t.settings.tabs.picow}</TabsTrigger>}
        {/* Kamery: admin-only, protože přepnutí kodeku / rychlosti / mazání pozic
            je na serveru za requireAdmin — neadminovi by karta byla k ničemu. */}
        {/* Konzole k AP: admin-only. Přes SSH jde přenastavit celá síť, takže server
            odmítne i samotný WS upgrade komukoli jinému než ADMINovi. */}
        {isAdmin && <TabsTrigger value="routeros">{t.settings.tabs.routeros}</TabsTrigger>}
        {isAdmin && <TabsTrigger value="foscam">{t.settings.tabs.foscam}</TabsTrigger>}
        {isAdmin && <TabsTrigger value="layout">Layout</TabsTrigger>}
      </TabsList>
      <TabsContent value="user" className="p-3">
        <UserTabPanel />
      </TabsContent>
      <TabsContent value="push" className="p-3">
        <PushTabPanel />
      </TabsContent>
      <TabsContent value="rules" className="p-3">
        <RulesTabPanel onClose={closeMethod} />
      </TabsContent>
      {isAdmin && (
        <TabsContent value="picow" className="p-3">
          <Suspense
            fallback={
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            }
          >
            <ConfigTabPanel />
          </Suspense>
        </TabsContent>
      )}
      {isAdmin && (
        <TabsContent value="routeros" className="p-3">
          <Suspense
            fallback={
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            }
          >
            <RouterOsTabPanel />
          </Suspense>
        </TabsContent>
      )}
      {isAdmin && (
        <TabsContent value="foscam" className="p-3">
          <FoscamTabPanel />
        </TabsContent>
      )}
      {isAdmin && (
        <TabsContent value="layout" className="p-3">
          <LayoutTabPanel onClose={closeMethod} />
        </TabsContent>
      )}
    </Tabs>
  );
};

export default React.memo(Settings);
